import { Button } from "components/Button/Button";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { Text } from "components/Form/components/Text/Text";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import {
    AllowedValueRow,
    normalizeAllowedValuesRows,
} from "config/resources/objectTemplateAttributeAllowedValues";
import { ChangeEvent, useCallback } from "react";
import "./AllowedValuesEditor.scss";

/**
 * Compact "form-array" editor for an OBJECT_TEMPLATE_ATTRIBUTE `allowed_values` set.
 *
 * Renders a removable list of rows — each row a **value** (the stored `const`) and an optional
 * **label** (dropdown display text) — plus a "+ Add allowed value" button. Sits in the compact
 * attribute side-panel, so it flows in-document (no absolute/overlay positioning like the Monaco
 * code editor, which used to cover the Save button). The form field value is the structured list
 * of `{ const, label }` rows; (de)serialisation to the stored `{ const, label? }` shape lives in
 * `objectTemplateAttributeAllowedValues.ts`.
 */
export const AllowedValuesEditor = ({ input, fieldProps, fieldAttr, meta }: FormComponentProps) => {
    const rows: AllowedValueRow[] = normalizeAllowedValuesRows(input.value);
    const isReadOnly = Boolean(fieldProps?.isReadOnly || fieldAttr?.disabled);
    const label = fieldProps?.label ?? "Allowed values";
    const hasError = Boolean((fieldProps?.validateOnMount || meta?.touched) && meta?.error);

    const commit = useCallback(
        (next: AllowedValueRow[]) => {
            input.onChange(next);
            input.onBlur?.();
        },
        [input]
    );

    const updateRow = useCallback(
        (index: number, key: keyof AllowedValueRow, value: string) => {
            const next = rows.map((row, i) => (i === index ? { ...row, [key]: value } : row));
            commit(next);
        },
        [rows, commit]
    );

    const removeRow = useCallback(
        (index: number) => {
            commit(rows.filter((_, i) => i !== index));
        },
        [rows, commit]
    );

    const addRow = useCallback(() => {
        commit([...rows, { const: "", label: "" }]);
    }, [rows, commit]);

    return (
        <div
            className={`allowedValuesEditor${hasError ? " allowedValuesEditor--error" : ""}`}
            data-testid={input.name}
        >
            <div className="allowedValuesEditor__label">{label}</div>

            {rows.length > 0 && (
                <div className="allowedValuesEditor__rows">
                    <div className="allowedValuesEditor__header">
                        <span className="allowedValuesEditor__col-label">Value</span>
                        <span className="allowedValuesEditor__col-label">Label</span>
                        <span className="allowedValuesEditor__col-spacer" />
                    </div>
                    {rows.map((row, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div className="allowedValuesEditor__row" key={index}>
                            <Text
                                name={`${input.name}-${index}-value`}
                                inputProps={{
                                    value: row.const,
                                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                                        updateRow(index, "const", event.target.value),
                                    isReadOnly,
                                }}
                                attributes={{ placeholder: "value" }}
                            />
                            <Text
                                name={`${input.name}-${index}-label`}
                                inputProps={{
                                    value: row.label,
                                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                                        updateRow(index, "label", event.target.value),
                                    isReadOnly,
                                }}
                                attributes={{ placeholder: "label (optional)" }}
                            />
                            {!isReadOnly && (
                                <Tooltip overlay="Remove">
                                    <span className="allowedValuesEditor__remove">
                                        <Button
                                            icon={IconCollection.deleteOutline}
                                            variation="clear"
                                            onClick={() => removeRow(index)}
                                            labelOnlyAria
                                            label={`Remove allowed value ${index + 1}`}
                                        />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!isReadOnly && (
                <div className="allowedValuesEditor__add">
                    <Button
                        icon={IconCollection.plus}
                        variation="clear"
                        onClick={addRow}
                        label="Add allowed value"
                    />
                </div>
            )}

            {hasError && <div className="allowedValuesEditor__errorText">{meta.error}</div>}
        </div>
    );
};
